import gsap from 'gsap';
import gql from 'graphql-tag';
import { Splide, SplideTrack } from 'react-splide-ts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { scaleVal } from '@/core/utils';
import VideoCarouselItem from './VideoCarouselItem';
import ArrowRight from '@assets/svg/arrow-right.svg';
import { VideoCarouselFragment } from '@graphqlTypes';
import { CarouselPagination, Container, RevealWrapper, SectionHeader } from '@components';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';
import { useClientMount } from '@/providers/ClientMountProvider';

import styles from './VideoCarousel.module.scss';
import className from 'classnames/bind';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
const cx = className.bind(styles);

type VideoCarouselProps = Omit<VideoCarouselFragment, '__typename' | 'videos'> & {
  videos?: Omit<VideoCarouselFragment['videos'][number], '__typename'>[];
  largeTitle?: boolean;
};

export default function VideoCarousel(props: VideoCarouselProps) {
  const { eyebrow, headline, videos, body, largeTitle } = props;
  const showHeader = Boolean(eyebrow || headline || body);
  const { mounted } = useClientMount();
  const { isMobile, viewWidth, isReady } = useViewSize();
  const { labels } = usePageWrapperData();
  const videoRefs = useRef<HTMLButtonElement[]>([]);
  const [collapsed, setCollapsed] = useState(true);
  const [showToggle, setShowToggle] = useState(false);

  const rootEl = useRef<HTMLElement>(null);
  const heights = useRef<{ collapsed: number; expanded: number }>({ collapsed: 0, expanded: 0 });
  // Ref for the carousel wrapper to animate height
  const carouselWrapperEl = useRef<HTMLDivElement>(null);
  const setVideoRefs = (el: HTMLButtonElement, index: number) => {
    if (el) videoRefs.current[index] = el;
  };

  const handleToggle = useCallback(() => {
    if (!mounted || !isReady) return;
    
    gsap.to(carouselWrapperEl.current, {
      height: collapsed ? heights.current.expanded : heights.current.collapsed,
      duration: 0.5,
      ease: 'power2.inOut',
    });
    setCollapsed(!collapsed);
  }, [mounted, isReady, collapsed]);

  const calculateHeights = useCallback(() => {
    if (!mounted || !isReady) return;
    if (!carouselWrapperEl.current || !videoRefs.current.length) return;
      const collapsedHeight = videoRefs.current.slice(0, 2).reduce((acc, el) => {
      if (el) acc += el.getBoundingClientRect().height;
      return acc;
    }, 0);
    const expandedHeight = videoRefs.current.reduce((acc, el) => {
      if (el) acc += el.getBoundingClientRect().height;
      return acc;
    }, 0) + scaleVal(48);
    heights.current = { collapsed: collapsedHeight, expanded: expandedHeight };
    gsap.set(carouselWrapperEl.current, {
      height: collapsed ? heights.current.collapsed : heights.current.expanded,
    });
  }, [mounted, isReady, collapsed]);

  useEffect(() => {
    if (!mounted || !isReady) return;
    
    if (isMobile) {
      calculateHeights();
    } else {
      gsap.set(carouselWrapperEl.current, { height: '' });
      setCollapsed(true);
    }
    setShowToggle(isMobile && videos?.length > 2);
  }, [mounted, isReady, isMobile, viewWidth, calculateHeights]);

  // -------------------- Splide

  const splideOptions = {
    type: 'slide',
    arrows: true,
    pagination: true,
    speed: 500,
    flickMaxPages: 1,
    flickPower: 250,
    updateOnMove: true,
    perPage: 2,
    perMove: 2,
    gap: '3rem',
    padding: {
      left: '5rem',
      right: '5rem',
    },
    breakpoints: {
      1025: {
        padding: {
          left: '4rem',
          right: '4rem',
        },
        arrows: false
      },
      769: {
        destroy: true,
      },
    }
  };

  // Override accessibility attributes
  useEffect(() => {
    if (!mounted || !isReady) return;
    if (!rootEl.current) return;
    const lists = rootEl.current.querySelectorAll('.splide__list');
    lists.forEach(list => {
      list.setAttribute('role', 'list');
    });
    const slides = rootEl.current.querySelectorAll('.splide__slide');
    slides.forEach(slide => {
      slide.setAttribute('role', 'listitem');
    });
  }, [mounted, isReady]);

  // -------------------- Animation

  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // -------------------- Render

  return (
    <section className={cx(["video-carousel"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        {showHeader && (
          <Container>
            <SectionHeader
              largeTitle={largeTitle}
              headline={headline}
              eyebrow={eyebrow}
              subhead={body}
              isRevealEnabled={true}
              isInView={isInView}
            />
          </Container>
        )}
        <div className={cx(["video-carousel__wrapper"])} ref={carouselWrapperEl}>
          <Splide
            hasTrack={false}
            options={splideOptions}
          >
            <SplideTrack>
              {videos?.map((video, index) => (
                <VideoCarouselItem key={index} {...video} ref={(el) => setVideoRefs(el, index)} />
              ))}
            </SplideTrack>
            <Container className={cx(["video-carousel__pagination"])}>
              <CarouselPagination />
            </Container>
          </Splide>
          {showToggle && (
            <div className={cx(["toggle-overlay", { expanded: !collapsed }])}>
              <button className={cx(["toggle", "inline-link"])} onClick={handleToggle}>
                <span>{collapsed ? labels?.viewMoreLabel : labels?.viewLessLabel}</span>
                <ArrowRight className={cx(["icon"])} />
              </button>
            </div>
          )}
        </div>
      </RevealWrapper>
    </section>
  );
}

VideoCarousel.fragments = {
  entry: gql`
    fragment VideoCarousel on FlexiblePageContentContentVideoCarouselLayout {
      eyebrow
      headline
      body
      videos {
        title
        videoEmbed {
          videoType
          youtubeVideoId
          videoUrl
          videoThumbnail {
            ...ACFMedia
          }
          youtubeThumbnail {
            ...ACFMedia
          }
        }
      }
    }
  `,
};
